import Calendar from "../../components/Calendar.tsx";
import {
  endOfDay,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfWeek,
} from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useCalendar } from "../../api/calendarApi.ts";
import { useParams, useSearchParams } from "react-router";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { CalendarEvent, CalendarViewType } from "../../components/calendar.ts";
import { useResponsiveCalendar } from "../../hooks/useResponsiveCalendar.ts";

export const CalendarPage = () => {
  const { dateFnsLocale } = useLanguageStore();
  const { userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { calendarViewType } = useResponsiveCalendar();

  // Parse date from query string or default to today
  const queryDate = searchParams.get("date");
  const initialDate =
    queryDate && isValid(parseISO(queryDate))
      ? parseISO(queryDate)
      : new Date();

  const [currentDate, setCurrentDate] = useState(initialDate);

  // Keep URL in sync when currentDate changes
  useEffect(() => {
    const formatted = format(currentDate, "yyyy-MM-dd");
    if (searchParams.get("date") !== formatted) {
      setSearchParams({ date: formatted });
    }
  }, [currentDate, searchParams, setSearchParams]);

  function getCalendarQueryRange(date: Date) {
    if (calendarViewType === CalendarViewType.WEEK) {
      return {
        startDate: startOfDay(startOfWeek(date, { locale: dateFnsLocale })),
        endDate: endOfDay(endOfWeek(date, { locale: dateFnsLocale })),
      };
    }
    return { startDate: startOfDay(date), endDate: endOfDay(date) };
  }

  const { startDate, endDate } = getCalendarQueryRange(currentDate);
  const { data: calendarEntries, refetch } = useCalendar(
    startDate,
    endDate,
    userId,
  );

  const events = useMemo<CalendarEvent[]>(
    () =>
      (calendarEntries ?? []).map((entry) => ({
        from: entry.from,
        to: entry.to,
        title: entry.title,
        color: entry.booked ? "secondary" : "primary",
      })),
    [calendarEntries],
  );

  const handleDateChange = (newDate: Date) => {
    setCurrentDate(newDate);
    refetch().catch((reason) => console.error(reason));
  };

  return (
    <div className="bg-soluud-background min-h-screen p-4 py-8 md:p-8">
      <div className="bg-soluud-background-card mx-auto max-w-7xl rounded-xl p-4 shadow-xl sm:p-6 lg:p-8">
        <Calendar
          events={events}
          currentDate={currentDate}
          onDateChange={handleDateChange}
          viewType={calendarViewType}
          locale={dateFnsLocale}
          showHeading
        />
      </div>
    </div>
  );
};
